/*
 * AZP-7008 — look up a part by the numbers printed on it or on the appliance.
 *
 * Shopify's search never sees these: metafields are not indexed, tags cap at 250, and a
 * part fits ~1,200 appliance models. Pushing them into descriptions would add ~18 KB of
 * text per product and blow the field limit on 21 of them.
 *
 * So the lookup runs against the same static index the collection facet uses:
 *   partnumbers.json   1,607 replacement part numbers — one file, fetched on first use
 *   models-manifest    where each shard of model numbers starts, ~700 bytes
 *   models-p<nn>       185,613 appliance model numbers, one balanced shard per lookup
 *
 * Results fill Shopify's own dropdown in place of its empty state, and only then — when
 * Shopify found nothing the index can answer. Everything it does find is left alone
 * (AZP-7331).
 */
(function () {
  "use strict";

  var MIN_CHARS = 3;
  // Handed over by snippets/part-lookup.liquid so the line stays translatable.
  var searchingText = "";
  // The frame the matches go into, cloned per injection (snippets/part-lookup.liquid).
  var frame = null;
  var CARD_SECTION = "model-suggest-card";
  // Both caps are the ones sections/predictive-search.liquid gives the native panel,
  // so a lookup fills the same grid to the same depth.
  var MAX_HITS = 6;
  var MAX_KEYWORDS = 5;

  function PartLookup(config) {
    this.base = config.base;
    this.version = config.version;
    this.catalog = null;
    this.partNumbers = null;
    this.manifest = null;
    this.shards = {};
    this.pending = {};
    this.loading = null;
    this.catalogLoading = null;
  }

  PartLookup.prototype.url = function (name) {
    var url = this.base + name;
    return this.version ? url + "?v=" + encodeURIComponent(this.version) : url;
  };

  PartLookup.prototype.fetchJSON = function (name) {
    return fetch(this.url(name))
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .catch(function () {
        return null;
      });
  };

  /* Part numbers and the shard map are pulled once, on the first keystroke that could
   * match — never on page load, so a shopper who does not use search pays nothing. */
  PartLookup.prototype.ready = function () {
    var self = this;
    if (this.loading) return this.loading;

    this.loading = Promise.all([
      this.fetchJSON("partnumbers.json"),
      this.fetchJSON("models-manifest.json"),
    ]).then(function (results) {
      self.partNumbers = (results[0] && results[0].n) || {};
      self.manifest = results[1];
    });

    return this.loading;
  };

  /* The catalogue is 348 KB against the 85 KB of the two files above, and it is read for
   * one purpose only: turning a part-number hit into a product. A model number is served
   * out of its own shard and never touches it. So it is fetched when a part number
   * actually matches, not on every keystroke — which is what a shopper typing a model
   * number does (AZP-7331). */
  PartLookup.prototype.matchesPartNumber = function (query) {
    var key = sortKey(query);
    return Object.keys(this.partNumbers).some(function (number) {
      return sortKey(number).indexOf(key) === 0;
    });
  };

  PartLookup.prototype.catalogue = function (query) {
    var self = this;
    if (this.catalog) return Promise.resolve(this.catalog);
    if (!this.matchesPartNumber(query)) return Promise.resolve(null);

    if (!this.catalogLoading) {
      this.catalogLoading = this.fetchJSON("catalog.json").then(function (data) {
        self.catalog = data || [];
        return self.catalog;
      });
    }
    return this.catalogLoading;
  };

  /* Models are filed in one sorted sequence cut into shards of roughly equal size, and
   * the manifest holds the first key of each. Which shard a query needs is a binary
   * search over ~48 strings, so a lookup costs one file rather than a walk down a tree of
   * guessed filenames.
   *
   * Separators are ignored when choosing the shard, and only there: a shopper types
   * `110875` for a machine badged `110.875`. The models themselves stay verbatim — 617 of
   * them collide once separators go. */
  function sortKey(text) {
    return String(text || "").toUpperCase().replace(/[^A-Z0-9]+/g, "");
  }

  PartLookup.prototype.shardFor = function (query) {
    var bounds = this.manifest && this.manifest.b;
    if (!bounds || !bounds.length) return null;
    var key = sortKey(query);
    var low = 0;
    var high = bounds.length - 1;
    while (low < high) {
      var mid = Math.ceil((low + high) / 2);
      if (bounds[mid] <= key) low = mid;
      else high = mid - 1;
    }
    return "models-p" + (low < 10 ? "0" + low : low);
  };

  PartLookup.prototype.shard = function (query) {
    var self = this;
    var name = this.shardFor(query);
    if (!name) return Promise.resolve(null);

    if (this.shards[name] !== undefined) return Promise.resolve(this.shards[name]);
    if (this.pending[name]) return this.pending[name];

    this.pending[name] = this.fetchJSON(name + ".json").then(function (data) {
      self.shards[name] = data;
      delete self.pending[name];
      return data;
    });

    return this.pending[name];
  };

  /* Answers with the products to show and the numbers that led to them. The numbers are
   * the panel's keyword column: Shopify has no completions to offer for a query it found
   * nothing for, and the column would otherwise be empty next to a full grid (AZP-7345).
   * They are collected on the same pass and capped on their own, so a query that matches
   * six products through one model number still offers only that one. */
  PartLookup.prototype.search = function (term, limit) {
    var self = this;
    var query = String(term || "").trim();
    if (query.length < MIN_CHARS) return Promise.resolve({ hits: [], numbers: [] });

    return this.ready()
      .then(function () {
        // The shard and the catalogue are independent of each other, so a query that
        // needs both does not pay for them one after the other.
        return Promise.all([self.shard(query), self.catalogue(query)]);
      })
      .then(function (loaded) {
        var shard = loaded[0];
        var seen = {};
        var hits = [];
        var numbers = [];

        var cap = limit || MAX_HITS;

        /* Takes the product itself, not a position: the two indexes below count
         * in different spaces. `partnumbers.json` points into the catalogue,
         * while a shard points into its own `p` list — a shard is fetched on its
         * own, by a shopper who typed a model number, so it carries the handful
         * of products it needs rather than depending on the catalogue's order. */
        function push(product, matched, kind) {
          if (hits.length >= cap) return;
          if (!product || seen[product.h]) return;
          seen[product.h] = true;
          hits.push({ handle: product.h, title: product.t, matched: matched, kind: kind });
        }

        function pushNumber(value, kind) {
          if (numbers.length >= MAX_KEYWORDS) return;
          for (var i = 0; i < numbers.length; i++) {
            if (numbers[i].value === value) return;
          }
          numbers.push({ value: value, kind: kind });
        }

        // Part numbers first: an exact one is the strongest signal a shopper can give.
        Object.keys(self.partNumbers).forEach(function (number) {
          if (sortKey(number).indexOf(sortKey(query)) !== 0) return;
          pushNumber(number, "part");
          self.partNumbers[number].forEach(function (slot) {
            push(self.catalog && self.catalog[slot], number, "part");
          });
        });

        if (shard && shard.m) {
          Object.keys(shard.m).forEach(function (model) {
            // Compared without separators so `110875` still reaches `110.87531702`.
            // Both keys survive as distinct suggestions; nothing is merged.
            if (sortKey(model).indexOf(sortKey(query)) !== 0) return;
            pushNumber(model, "model");
            shard.m[model].forEach(function (index) {
              push(shard.p[index], model, "model");
            });
          });
        }

        return { hits: hits, numbers: numbers };
      });
  };

  /* ---- presentation ---- */

  /* Cards are rendered by the theme, by the same snippet Shopify's own predictive
   * results use. Building them here produced a bare list with no image, price or add to
   * cart — next to the native dropdown it read as a different feature (AZP-7345).
   *
   * `alive` is asked again after every fetch: the card arrives late enough that the
   * shopper may have typed on, and Shopify may have rebuilt the box from under us. */
  function fillCards(list, hits, alive) {
    hits.forEach(function (hit, index) {
      var slot = document.createElement("li");
      slot.className = "bs-suggest-product part-lookup-card-loading";
      list.appendChild(slot);

      fetch("/products/" + hit.handle + "?section_id=" + CARD_SECTION)
        .then(function (response) {
          return response.ok ? response.text() : null;
        })
        .then(function (html) {
          if (!alive()) return; // a later keystroke won
          if (!html) {
            slot.remove();
            return;
          }
          var holder = document.createElement("div");
          holder.innerHTML = html.trim();
          var card = holder.querySelector("[data-suggest-card]") || holder.firstElementChild;
          if (!card) {
            slot.remove();
            return;
          }
          // What arrow-key navigation moves between, and what the input points at.
          card.id = "predictive-search-option-product-" + (index + 1);
          slot.replaceWith(card);
        })
        .catch(function () {
          slot.remove();
        });
    });
  }

  /* A number leads to the facet rather than to `/search`, which cannot see it: the same
   * place pressing Enter on it ends up (see `redirectNumberSearch`). */
  function numberUrl(number) {
    var key = number.kind === "part" ? "az_part" : "az_model";
    return "/collections/all#" + key + "=" + encodeURIComponent(number.value);
  }

  function fillKeywords(list, numbers) {
    numbers.forEach(function (number, index) {
      var item = document.createElement("li");
      item.id = "predictive-search-option-query-" + (index + 1);
      item.className = "bs-suggest-keyword";
      item.setAttribute("role", "option");
      item.setAttribute("aria-selected", "false");

      var link = document.createElement("a");
      link.className = "bs-suggest-keyword-link";
      link.href = numberUrl(number);
      link.tabIndex = -1;
      link.textContent = number.value;

      item.appendChild(link);
      list.appendChild(item);
    });
  }

  /* AZP-7331 — the matches go INSIDE Shopify's own results box, in place of its empty
   * state, rather than into a second panel of this feature's own.
   *
   * A second panel cannot be made to line up: on desktop the box is laid out by
   * `.header .head-slot-mid .predictive-search-header[data-predictive-search]`
   * (bluestars-homepage.css), a full-bleed `position: fixed` rule keyed on an attribute
   * only that box carries. AZP-7077 saw the copy sitting under it and hid the panel
   * outright, on the grounds that "matches already come back in the native results".
   * That holds for a part number — it is in the `variants.sku` Shopify searches — but
   * never for a model number, which lives only in this static index. Hiding the panel
   * therefore hid the only answer a model number has, and the shopper got
   * "No results found" for a number the store can fit.
   *
   * Stepping in only when Shopify found nothing keeps it to one list at a time: no
   * overlap to lay out, and no decision to make about how two result sets would merge.
   */
  function attach(input, lookup) {
    var form = input.closest("form");
    if (!form) return;

    // AZP-7077 moved the dropdown out of the form and up into <predictive-search>,
    // so the box is looked up on the custom element when there is one.
    var host = input.closest("predictive-search") || form;
    var native = host.querySelector("[data-predictive-search]");
    if (!native) return;

    var current = { term: "", hits: [], numbers: [] };

    /* Shopify answers in a few milliseconds and this index answers in a few hundred, so
     * without this the shopper is told "No results found" and then handed results — the
     * flash AZP-7331 is named after. Its verdict is only withheld while an answer is
     * still coming; once there is none, it is the honest one and it comes back.
     *
     * The flag is a class on the box itself, so Shopify rewriting the box's innerHTML on
     * the next keystroke does not clear it. */
    var settle = null;
    function waiting(on) {
      native.classList.toggle("part-lookup-waiting", on);
      clearTimeout(settle);
      if (!on) {
        var note = native.querySelector(".part-lookup-searching");
        if (note) note.remove();
        return;
      }
      // A CDN that never answers must not leave the dropdown blank for good.
      settle = setTimeout(function () { waiting(false); }, 4000);
    }

    /* Something has to stand in the withheld line's place, or the dropdown is a blank
     * box for the few hundred milliseconds the index takes. */
    function showSearching() {
      if (!native.classList.contains("part-lookup-waiting")) return;
      if (!searchingText) return;
      if (native.querySelector(".part-lookup-searching")) return;
      var empty = native.querySelector(".bs-suggest-empty");
      if (!empty) return; // Shopify found things; there is no gap to fill

      var note = document.createElement("p");
      note.className = "part-lookup-searching";
      note.textContent = searchingText;
      empty.parentNode.insertBefore(note, empty);
    }

    /* The frame is taken apart rather than dropped in whole: the grid stands exactly
     * where the empty line stood, inside `.bs-suggest-results`, and the keyword column
     * goes in front of it as its own child of `.bs-suggest-inner`. That is the shape the
     * native panel has, and the two-column desktop layout is keyed on it — the column
     * divider is `.bs-suggest-keywords + .bs-suggest-results` and the full-width fallback
     * is `.bs-suggest-results:first-of-type` (bluestars-homepage.css). */
    function inject() {
      if (!frame || !current.hits.length || input.value !== current.term) return;

      // Shopify answered: its own results stand. Ours would be a second list of the
      // same products, since a part number is in the SKU it already searches.
      var empty = native.querySelector(".bs-suggest-empty");
      if (!empty) return;
      if (native.querySelector("[data-part-lookup-list]")) return; // already in

      var parts = frame.content.cloneNode(true);
      var keywords = parts.querySelector("[data-part-lookup-keywords]");
      var grid = parts.querySelector("[data-part-lookup-list]");
      var all = parts.querySelector("[data-part-lookup-all]");
      var results = empty.parentNode;

      empty.replaceWith(grid);
      if (all) results.appendChild(all);
      if (keywords && current.numbers.length) {
        fillKeywords(keywords.querySelector("[data-part-lookup-keyword-list]"), current.numbers);
        results.parentNode.insertBefore(keywords, results);
      }
      waiting(false);

      var term = current.term;
      fillCards(grid, current.hits, function () {
        return input.value === term && grid.isConnected;
      });
    }

    // Shopify rebuilds the whole box on every keystroke (`predictive-search.js` assigns
    // its innerHTML), taking the group and the searching line with it — so both are put
    // back after each of its renders instead of once per lookup. Our own nodes land in
    // the box too and notify this same observer; the guards above stop that looping.
    function sync() {
      showSearching();
      inject();
    }
    new MutationObserver(sync).observe(native, { childList: true, subtree: true });

    var timer = null;
    var run = function () {
      var term = input.value;
      lookup.search(term).then(function (found) {
        if (input.value !== term) return; // a later keystroke already won
        current = { term: term, hits: found.hits, numbers: found.numbers };
        // Nothing to add: Shopify's empty state is the honest answer, let it through.
        // Otherwise hold it back until the grid is actually in — Shopify may not have
        // rendered yet, and the observer will inject once it does.
        if (!found.hits.length) waiting(false);
        else inject();
      });
    };

    input.addEventListener("input", function () {
      clearTimeout(timer);
      // Set before the debounce, not after it: Shopify is already fetching, and 180 ms
      // is long enough for its verdict to paint.
      waiting(input.value.trim().length >= MIN_CHARS);
      showSearching();
      timer = setTimeout(run, 180);
    });

    if (input.value) run();
  }

  /* Shopify's own search cannot see these numbers, so pressing Enter on one lands on an
   * empty results page — and because Shopify found nothing, the theme renders no sidebar,
   * no count and no pagination there either, none of which JS can put back.
   *
   * The collection page has all of that already, and the facet built for it understands
   * the same numbers, so a number query is sent there instead. A word query is left to
   * Shopify, which handles those better than this index would. */
  function redirectNumberSearch(lookup) {
    var params = new URLSearchParams(window.location.search);
    var term = (params.get("q") || "").trim();
    if (window.location.pathname.indexOf("/search") !== 0 || !term) {
      document.documentElement.classList.remove("part-lookup-pending");
      return;
    }

    lookup
      .search(term, 1)
      .catch(function () {
        return { hits: [], numbers: [] };
      })
      .then(function (found) {
        var hits = found.hits;
        if (!hits.length) {
          // Nothing matched a number: Shopify's own empty state is the honest answer.
          document.documentElement.classList.remove("part-lookup-pending");
          return;
        }
        // Handed over in the fragment, never the query string. A query string would mint
        // a crawlable URL per model number — 185,613 of them, all rendering the same
        // server-side HTML as the bare collection because the narrowing happens in the
        // browser. Liquid cannot read an arbitrary query parameter, so the robots tag in
        // `theme.liquid` could not have told them apart; a fragment never reaches the
        // server and is not a separate URL to begin with.
        window.location.replace(
          numberUrl({ value: hits[0].matched, kind: hits[0].kind })
        );
      });
  }

  document.addEventListener("DOMContentLoaded", function () {
    var config = document.querySelector("[data-part-lookup]");
    if (!config || !config.dataset.indexBase) return;

    searchingText = config.dataset.searchingText || "";
    frame = document.querySelector("template[data-part-lookup-template]");

    var lookup = new PartLookup({
      base: config.dataset.indexBase,
      version: config.dataset.indexVersion || "",
    });

    document.querySelectorAll('input[name="q"]').forEach(function (input) {
      attach(input, lookup);
    });

    redirectNumberSearch(lookup);
  });
})();
